import { useCallback, useMemo, useRef, useState } from "react";

import { isEqual } from "lodash-es";

import { getAllKeys } from "src/cases/caseView/blocks/cellTypes";
import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";

type CellKey = `${number}:${string}`;

const makeCellKey = (rowIndex: number, columnKey: string): CellKey =>
  `${rowIndex}:${columnKey}`;

const parseCellKey = (
  key: CellKey,
): { rowIndex: number; columnKey: string } => {
  const separatorIndex = key.indexOf(":");
  return {
    rowIndex: Number(key.slice(0, separatorIndex)),
    columnKey: key.slice(separatorIndex + 1),
  };
};

type RowOperation =
  | { type: "delete"; index: number }
  | { type: "duplicate"; index: number }
  | { type: "add" };

export type UseTableBlockEditsReturn = {
  editedData: TableBlockMultiColumnRecord;
  modifiedCells: Set<string>;
  hasChanges: boolean;
  updateCell: (rowIndex: number, columnKey: string, value: unknown) => void;
  addRow: () => void;
  deleteRow: (rowIndex: number) => void;
  duplicateRow: (rowIndex: number) => void;
  resetEdits: () => void;
};

export const useTableBlockEdits = (
  originalData: TableBlockMultiColumnRecord,
): UseTableBlockEditsReturn => {
  // null = no local row changes, derive workingData from originalData directly.
  // This keeps data in sync with polling when the user isn't actively editing.
  const [localData, setLocalData] =
    useState<TableBlockMultiColumnRecord | null>(null);
  const [cellEdits, setCellEdits] = useState<Map<CellKey, unknown>>(new Map());

  const workingData = localData ?? originalData;

  const originalDataRef = useRef(originalData);
  originalDataRef.current = originalData;

  const editsByRow = useMemo(
    () =>
      [...cellEdits.entries()].reduce<Map<number, Record<string, unknown>>>(
        (grouped, [key, value]) => {
          const { rowIndex, columnKey } = parseCellKey(key);
          const existing = grouped.get(rowIndex);
          if (existing) {
            existing[columnKey] = value;
          } else {
            grouped.set(rowIndex, { [columnKey]: value });
          }
          return grouped;
        },
        new Map<number, Record<string, unknown>>(),
      ),
    [cellEdits],
  );

  const editedData = useMemo(
    () =>
      workingData.map((row, rowIndex) => {
        const rowEdits = editsByRow.get(rowIndex);
        return rowEdits ? { ...row, ...rowEdits } : row;
      }),
    [workingData, editsByRow],
  );

  const modifiedCells = useMemo(() => new Set(cellEdits.keys()), [cellEdits]);

  const hasChanges = cellEdits.size > 0 || localData !== null;

  const workingDataRef = useRef(workingData);
  workingDataRef.current = workingData;

  const updateCell = useCallback(
    (rowIndex: number, columnKey: string, value: unknown) => {
      const key = makeCellKey(rowIndex, columnKey);
      const originalValue = workingDataRef.current[rowIndex]?.[columnKey];
      if (isEqual(value, originalValue)) {
        setCellEdits((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      } else {
        // Snapshot data on first real edit so polling can't shift row indices
        setLocalData((prev) => prev ?? originalDataRef.current);
        setCellEdits((prev) => {
          const next = new Map(prev);
          next.set(key, value);
          return next;
        });
      }
    },
    [],
  );

  const applyRowOperation = useCallback((op: RowOperation) => {
    setLocalData((prev) => {
      const current = prev ?? originalDataRef.current;
      switch (op.type) {
        case "delete":
          return current.filter((_, i) => i !== op.index);
        case "duplicate": {
          const rowCopy = { ...current[op.index] };
          return [
            ...current.slice(0, op.index + 1),
            rowCopy,
            ...current.slice(op.index + 1),
          ];
        }
        case "add": {
          const keys = getAllKeys(current);
          const emptyRow = Object.fromEntries(keys.map((k) => [k, null]));
          return [...current, emptyRow];
        }
      }
    });

    setCellEdits((prev) =>
      [...prev.entries()].reduce<Map<CellKey, unknown>>(
        (next, [key, value]) => {
          const { rowIndex, columnKey } = parseCellKey(key);

          if (op.type === "delete") {
            if (rowIndex === op.index) return next;
            const shifted = rowIndex > op.index ? rowIndex - 1 : rowIndex;
            next.set(makeCellKey(shifted, columnKey), value);
          } else if (op.type === "duplicate") {
            const shifted = rowIndex > op.index ? rowIndex + 1 : rowIndex;
            next.set(makeCellKey(shifted, columnKey), value);
            if (rowIndex === op.index) {
              next.set(makeCellKey(op.index + 1, columnKey), value);
            }
          } else {
            next.set(key, value);
          }

          return next;
        },
        new Map<CellKey, unknown>(),
      ),
    );
  }, []);

  const addRow = useCallback(
    () => applyRowOperation({ type: "add" }),
    [applyRowOperation],
  );

  const deleteRow = useCallback(
    (rowIndex: number) =>
      applyRowOperation({ type: "delete", index: rowIndex }),
    [applyRowOperation],
  );

  const duplicateRow = useCallback(
    (rowIndex: number) =>
      applyRowOperation({ type: "duplicate", index: rowIndex }),
    [applyRowOperation],
  );

  const resetEdits = useCallback(() => {
    setLocalData(null);
    setCellEdits(new Map());
  }, []);

  return {
    editedData,
    modifiedCells,
    hasChanges,
    updateCell,
    addRow,
    deleteRow,
    duplicateRow,
    resetEdits,
  };
};
